<template>
  <div>
    <div class="tw:w-full tw:bg-white">
      <div class="tw:md:max-w-screen-lg tw:lg:max-w-screen-xl tw:mx-auto tw:px-4 tw:pt-6">
        <div>
          <div ref="titleBlock" class="tw:flex tw:items-center tw:py-2">
            <p class="tw:text-[32px] tw:font-bold">{{ $t('app.rateInsights.name') }}</p>
            <UiTooltip
              theme="customdarkwithpadding"
              tooltip-position="top"
              :label="$t('rate_insights_tooltip')"
              class="tw:cursor-pointer tw:ml-1"
            >
              <InformationOutline class="tw:fill-none tw:stroke-gray-800 tw:h-[22px] tw:w-[22px] tw:ml-1" />
            </UiTooltip>
          </div>
          <p class="tw:flex tw:w-full tw:text-base tw:font-normal">
            {{ $t('published_rates_shown') }}
          </p>
        </div>
      </div>
    </div>
    <div
      class="tw:sticky tw:top-[100px] tw:sm:top-nav-h tw:z-30 tw:w-full tw:bg-white tw:transition-shadow tw:duration-200"
      :class="isSticky ? 'tw:shadow-lg' : ''"
    >
      <div class="tw:md:max-w-screen-lg tw:lg:max-w-screen-xl tw:mx-auto tw:px-4 tw:py-5">
        <RatesFilters
          :filters="filters"
          :aggregation="aggregation"
          :competitors="competitors"
          :channels="selectedChannels"
          :marketRate="marketRate"
          :dateRateOptions="timeRangeOptions"
          :selectedTimeRange="selectedTimeRange"
          :selectedAggregation="selectedAggregation"
          :disabled="isWaitingMode"
          @setMarketRate="value => setMarketRate(value)"
          @setAggregation="value => setAggregation(value)"
          @setChannelsIds="value => setChannelsIds(value)"
          @setCompetitorsIds="value => setCompetitorsIds(value)"
          @setTimeRange="value => setTimeRange(value)"
        />
      </div>
    </div>
    <div class="tw:mt-6 tw:md:max-w-screen-lg tw:lg:max-w-screen-xl tw:mx-auto tw:w-full tw:px-4">
      <div v-if="showEmptyState" class="tw:mt-8">
        <div class="tw:flex tw:items-center tw:py-1">
          <p class="tw:text-2xl tw:font-bold">{{ $t('ri_market_position_header') }}</p>
        </div>
        <p class="tw:text-base tw:font-normal tw:mb-6">{{ $t('ri_market_position_subtext') }}</p>
        <div class="tw:bg-white tw:border tw:border-gray-300 tw:rounded-xl">
          <div class="tw:flex tw:flex-col tw:items-center tw:text-center tw:py-4">
            <img :src="clockIcon" alt="clock icon" class="tw:w-10 tw:h-10 tw:mb-3" />
            <p class="tw:font-bold tw:text-base">{{ $t('ri_market_empty_state_header') }}</p>
            <p class="tw:text-sm tw:mt-1">{{ $t('ri_market_empty_state_subtext') }}</p>
            <div class="tw:flex tw:items-center tw:gap-2 tw:mt-3 tw:bg-gray-200 tw:rounded-lg tw:px-20 tw:py-3">
              <img :src="smallClockIcon" alt="small clock icon" class="tw:h-4 tw:w-4" />
              <span class="tw:text-xs tw:text-gray-600">{{ $t('ri_empty_state_est_time') }}</span>
            </div>
          </div>
          <div>
            <div class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-3 tw:gap-3 tw:p-4">
              <div
                v-for="labelKey in ['ri_hotel_avg_rate', 'ri_market_average', 'ri_rate_difference']"
                :key="labelKey"
                class="tw:bg-gray-100 tw:rounded-lg tw:px-5 tw:py-4"
              >
                <p class="tw:text-sm tw:font-bold tw:text-gray-400 tw:mb-2">{{ $t(labelKey) }}</p>
                <div class="tw:animate-pulse tw:space-y-3">
                  <div class="tw:h-8 tw:bg-gray-200 tw:rounded tw:w-3/4"></div>
                  <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-1/2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <MarketPositionCard
        v-else
        :your-avg-rate="yourAvgRate"
        :market-avg="marketAvg"
        :price-diff="priceDiff"
        :price-diff-pct="priceDiffPct"
        :status="marketPositionStatus"
        :has-low-market-data="hasLowMarketData"
      />

      <div v-if="showEmptyState" class="tw:mt-6 tw:bg-white tw:border tw:border-gray-300 tw:rounded-xl tw:p-6">
        <p class="tw:text-xl tw:font-bold tw:mb-1">{{ $t('rate_comparison_booking_sites') }}</p>
        <p class="tw:text-sm tw:text-gray-600 tw:mb-4">{{ $t('ri_booking_sites_empty_state_subtext') }}</p>
        <div class="tw:animate-pulse tw:space-y-3">
          <div class="tw:flex tw:gap-3" v-for="i in 5" :key="i">
            <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-1/4"></div>
            <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-1/6"></div>
            <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-1/6"></div>
            <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-1/6"></div>
            <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-1/6"></div>
          </div>
        </div>
      </div>

      <ChannelComponent
        v-else-if="rates !== undefined"
        :channels="selectedChannels"
        :competitors="visibleCompetitors"
        :filters="customFilters"
        :rates="getRates(rates, aggregationData, timestamp)"
        :formattedDate="getDatesToShow(rates, aggregationData, timestamp)"
        :available-days="Object.keys(rates)"
        :selected-timestamp="timestamp"
        :aggregation="aggregationData"
        @select-day="setTimestamp"
      />

      <PriceTrendChart
        :is-loading="showEmptyState"
        :competitors="competitorsWithColors"
        :series="getData(rates === undefined ? {} : rates, marketRate)"
        :categories="getDays(rates === undefined ? {} : rates)"
        :days="getDays(rates === undefined ? {} : rates)"
        :filters="customFilters"
        @select-day="setTimestamp"
      />
    </div>
    <div class="tw:mt-8 tw:w-full tw:bg-white">
      <div class="tw:md:max-w-screen-lg tw:lg:max-w-screen-xl tw:mx-auto tw:px-4 tw:py-8">
        <RecommendedActions />
      </div>
    </div>

    <FullScreenLoader v-show="isRatesFetching" />
  </div>
</template>
<script setup>
import { useI18n } from 'vue-i18n'
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRates, useSelectedBookingSites, useCompetitors } from '@/components/rateInsights/queries'
import { useRateInsights } from '@/components/rateInsights/useRateInsights.js'
import { calculateDates, getDateRange } from '@/components/rateInsights/utils.js'

import appAnalytics from '@/utils/tracking'
import { filter, max } from '@/utils/Utility.ts'

import FullScreenLoader from '@/components/base/loaders/FullScreenLoader.vue'
import RatesFilters from '@/components/rateInsights/rates/RatesFilters.vue'
import ChannelComponent from '@/components/rateInsights/rates/ChannelComponent.vue'
import MarketPositionCard from '@/components/rateInsights/rates/MarketPositionCard.vue'
import PriceTrendChart from '@/components/rateInsights/rates/PriceTrendChart.vue'
import RecommendedActions from '@/components/rateInsights/rates/RecommendedActions.vue'
import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'
import InformationOutline from '@/components/icons/InformationOutline.svg?component'
import { selectedAccommodationId, useAccommodationsByOrgId } from '@/layouts/queries'
import { filters } from '@/constants/riFilters.ts'
import { useSessionValues } from '@/composables/useSessionValues'
import { useMarketMetrics } from '@/components/rateInsights/useMarketMetrics.js'
import clockIcon from '@/assets/img/rate-insights/clock-icon.svg'
import smallClockIcon from '@/assets/img/rate-insights/small-clock-icon.svg'

const { t } = useI18n()

const titleBlock = ref(null)
const isSticky = ref(false)
let stickyObserver = null

onMounted(() => {
  stickyObserver = new IntersectionObserver(
    ([entry]) => {
      isSticky.value = !entry.isIntersecting
    },
    { threshold: 0 }
  )
  if (titleBlock.value) {
    stickyObserver.observe(titleBlock.value)
  }
})

onUnmounted(() => {
  stickyObserver?.disconnect()
})

const aggregation = [
  { id: 1, name: 'DAY', default: true, label: 'aggregation_day' },
  { id: 2, name: 'WEEK', default: false, label: 'aggregation_week' },
]

const { selectedChannels } = useSelectedBookingSites()
const { competitors } = useCompetitors(selectedAccommodationId)
const { currency, language, selectedOrganisationId } = useSessionValues()

const filterValues = Object.assign(
  {},
  ...filters.map(item => ({ [item.name]: item.options.filter(item => item.default)[0] }))
)
const isWaitingMode = computed(() => selectedChannels?.value?.length === 0)
const hasNoRates = ref(false)
const showEmptyState = computed(() => isRatesLoading.value || hasNoRates.value || isWaitingMode.value)
const timeRangeOptions = computed(() => [
  {
    id: 0,
    name: 'past_x_days',
    amount: 7,
    value: { past: true, unit: 'days' },
    default: false,
    label: t('past_x_days', { amount: 7 }),
  },
  {
    id: 1,
    name: 'next_x_days',
    amount: 7,
    value: { past: false, unit: 'days' },
    default: true,
    label: t('next_x_days', { amount: 7 }),
  },
  {
    id: 2,
    name: 'past_x_days',
    amount: 30,
    value: { past: true, unit: 'days' },
    default: false,
    label: t('past_x_days', { amount: 30 }),
  },
  {
    id: 3,
    name: 'next_x_days',
    amount: 30,
    value: { past: false, unit: 'days' },
    default: false,
    label: t('next_x_days', { amount: 30 }),
  },
  {
    id: 4,
    name: 'past_x_months',
    amount: 2,
    value: { past: true, unit: 'months' },
    default: false,
    label: t('past_x_months', { amount: 2 }),
  },
  {
    id: 5,
    name: 'next_x_months',
    amount: 2,
    value: { past: false, unit: 'months' },
    default: false,
    label: t('next_x_months', { amount: 2 }),
  },
  {
    id: 6,
    name: 'past_x_months',
    amount: 3,
    value: { past: true, unit: 'months' },
    default: false,
    label: t('past_x_months', { amount: 3 }),
  },
  {
    id: 7,
    name: 'next_x_months',
    amount: 3,
    value: { past: false, unit: 'months' },
    default: false,
    label: t('next_x_months', { amount: 3 }),
  },
])
const selectedTimeRange = ref(timeRangeOptions.value.filter(item => item.default)[0])
const marketRate = ref(filterValues['market_rates'].name || 'LOWEST_ALL')
const selectedAggregation = ref(aggregation.filter(item => item.default)[0])
const aggregationData = ref(selectedAggregation?.value.name)
const channelsIds = ref(selectedChannels?.value?.map(item => item.id).toString())
const { allFilters } = useRateInsights()
const { currentAccommodation } = useAccommodationsByOrgId(selectedOrganisationId)

const allCompetitors = computed(() => {
  const myAccommodation = [
    {
      id: selectedAccommodationId.value,
      name: currentAccommodation.value?.name || `Hotel Id ${selectedAccommodationId.value}`,
    },
  ]
  return [...myAccommodation, ...competitors.value]
})

watch(
  selectedChannels,
  newValue => {
    channelsIds.value = newValue?.map(item => item.id).toString()
  },
  { deep: true, immediate: true }
)

const competitorsIds = computed(() => allCompetitors?.value?.map(item => item.id).toString())
const shownCompetitors = ref(allCompetitors?.value?.map(item => item.id).toString())
const filteredCompetitors = computed(() => {
  const competitorsArr = competitorsIds.value.split(',')
  const filtered = shownCompetitors.value.split(',').filter(competitor => competitorsArr.indexOf(competitor) >= 0)

  return filtered.toString()
})

const colors = ['#007BC1', '#8291D3', '#FC9E15', '#599A19', '#FF8381', '#3F57BD']
const competitorsWithColors = computed(() => {
  return allCompetitors.value?.map((item, i) => ({ ...item, color: colors[i] }))
})
const visibleCompetitors = computed(() => {
  const shown = new Set(filteredCompetitors.value.split(','))
  return competitorsWithColors.value?.filter(c => shown.has(String(c.id)))
})

const startDate = ref(
  calculateDates({
    amount: selectedTimeRange.value.amount,
    unit: selectedTimeRange.value.value,
  }).from
)
const endDate = ref(
  calculateDates({
    amount: selectedTimeRange.value.amount,
    unit: selectedTimeRange.value.value,
  }).to
)

const timestamp = ref(
  calculateDates({
    amount: selectedTimeRange.value.amount,
    unit: selectedTimeRange.value.value,
  }).timestamp
)

const customFilters = computed(() => {
  return {
    channelIds: channelsIds.value,
    breakfast: allFilters.value['breakfast']?.name,
    roomType: allFilters.value['room_type']?.name,
    marketRates: marketRate.value,
  }
})

const setTimeRange = value => {
  selectedTimeRange.value = value
  startDate.value = calculateDates({ amount: value.amount, unit: value.value }).from
  endDate.value = calculateDates({ amount: value.amount, unit: value.value }).to
  timestamp.value = calculateDates({ amount: value.amount, unit: value.value }).timestamp
}
const setMarketRate = value => {
  marketRate.value = value
}
const setAggregation = value => {
  selectedAggregation.value = value
  aggregationData.value = value.name
  timestamp.value = calculateDates({
    amount: selectedTimeRange.value.amount,
    unit: selectedTimeRange.value.value,
  }).timestamp
}
const setChannelsIds = value => {
  channelsIds.value = value
}
const setCompetitorsIds = value => {
  if (!value.includes(selectedAccommodationId.value)) {
    shownCompetitors.value = selectedAccommodationId.value + ',' + value
  } else {
    shownCompetitors.value = value
  }
}

const setTimestamp = newTimestamp => {
  timestamp.value = newTimestamp
}

const { isRatesLoading, rates, isRatesFetching } = useRates({
  marketRate,
  aggregation: aggregationData,
  channels: channelsIds,
  competitors: filteredCompetitors,
  currency: currency,
  from: startDate,
  to: endDate,
})

const { yourAvgRate, marketAvg, priceDiff, priceDiffPct, marketPositionStatus, hasLowMarketData } = useMarketMetrics({
  rates,
  accommodationId: selectedAccommodationId,
  visibleCompetitors,
  selectedChannels,
  customFilters,
})

const getRates = (allRates, aggregation, currentTimestamp) => {
  const competitorsArr = filteredCompetitors.value.split(',')
  const channelsArr = channelsIds.value.split(',')
  const ratesForEmptyData = {}
  for (const item of competitorsArr) {
    ratesForEmptyData[item] = {}
    for (const key of channelsArr) {
      ratesForEmptyData[item][key] = {
        double: 0,
        doubleBreakfast: 0,
        multi: 0,
        multiBreakfast: 0,
        single: 0,
        singleBreakfast: 0,
      }
    }
  }

  if (aggregation === 'DAY') {
    return allRates[currentTimestamp]
      ? fillMissingData(ratesForEmptyData, allRates[currentTimestamp])
      : ratesForEmptyData
  } else {
    const keys = Object.keys(allRates)
    const currentWeek =
      keys.find(item => item.startsWith(currentTimestamp)) !== undefined
        ? keys.find(item => item.startsWith(currentTimestamp))
        : keys[0]

    return allRates[currentWeek] ? fillMissingData(ratesForEmptyData, allRates[currentWeek]) : ratesForEmptyData
  }
}

const fillMissingData = (ratesForEmptyData, allRates) => {
  for (const [key] of Object.entries(ratesForEmptyData)) {
    if (allRates[key]) {
      ratesForEmptyData[key] = allRates[key]
    }
  }
  return ratesForEmptyData
}

const getDatesToShow = (allRates, aggregation, timestamp) => {
  if (aggregation === 'DAY') {
    let date = new Date(timestamp * 1000)
    return date.toLocaleString(language.value, { day: 'numeric', month: 'short', timeZone: 'UTC' })
  } else {
    const keys = Object.keys(allRates)
    const currentWeek = keys.find(item => item.startsWith(timestamp))?.split('-')
    let start
    let end

    if (keys.length > 0 && currentWeek?.length > 0) {
      start = new Date(currentWeek[0] * 1000)
      end = new Date(currentWeek[1] * 1000)
    } else if (keys.length > 0) {
      start = new Date(keys[0]?.split('-')[0] * 1000)
      end = new Date(keys[0]?.split('-')[1] * 1000)
    } else {
      start = new Date(startDate.value)
      end = new Date(endDate.value)
    }

    return `${start.toLocaleString(language.value, {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    })} - ${end.toLocaleString(language.value, { day: 'numeric', month: 'short', timeZone: 'UTC' })}`
  }
}

const getDays = allRates => {
  const days = getDateRange({ days: Object.keys(allRates) })

  if (days.length > 0) return days

  return Array.from(Array(7).keys()).map(idx => {
    const d = new Date()
    d.setDate(d.getDate() - d.getDay() + idx)
    return {
      date: new Date(d),
      timestamp: new Date(d).getTime(),
    }
  })
}

const getData = (allRates, marketRates) => {
  const competitors = JSON.parse(JSON.stringify(competitorsWithColors.value))
  const channels = selectedChannels ? selectedChannels?.value : []
  const breakfast = customFilters.value.breakfast
  const room_type = customFilters.value.roomType

  const rateType = `${room_type}${breakfast === 'included' ? 'Breakfast' : ''}`
  const series = []

  if (competitors.length) {
    competitors.map(competitor => {
      const competitorDaySeries = []
      const competitorId = competitor.id
      const competitorColor = competitor.color

      Object.keys(allRates).map(timestamp => {
        const competitorChannelRates = allRates[timestamp][competitorId]
        const competitorDayChannelRates = []

        if (channels.length) {
          channels.map(channel => {
            const channelRates =
              competitorChannelRates && competitorChannelRates[channel.id] ? competitorChannelRates[channel.id] : null
            competitorDayChannelRates.push(channelRates ? channelRates[rateType] : 0)
          })
        }

        let rateShown
        if (marketRates === 'lowest' || marketRates === 'home') {
          rateShown = Math.min.apply(null, filter(competitorDayChannelRates, Boolean))
        } else {
          rateShown = max(competitorDayChannelRates)
        }

        if (!rateShown || rateShown === Infinity || rateShown === 0) {
          rateShown = null
        }

        competitorDaySeries.push(rateShown)
      })

      series.push({
        name: competitor.name,
        data: competitorDaySeries,
        competitorId: competitorId,
        color: competitorColor,
        marker: {
          symbol: 'circle',
        },
      })
    })
  }

  const allSeries = series.filter(serie => {
    const hasData = serie.data.some(val => val !== null && val !== 0)

    return hasData && serie.data.length > 0
  })

  const hasData = allSeries.length > 0
  const wasNoData = hasNoRates.value
  hasNoRates.value = !hasData

  if (!hasData && !wasNoData && selectedAccommodationId.value) {
    appAnalytics.track(appAnalytics.events.RI_NO_DATA_FOR_FILTERS, {
      accommodationId: selectedAccommodationId.value,
    })
  }

  return hasData
    ? allSeries
    : [
        {
          data: [50, null, null, null, null, 100],
          connectNulls: false,
          color: 'rgba(0,0,0,0)',
          marker: {
            width: '1px',
          },
          enableMouseTracking: false,
        },
      ]
}
</script>
